import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';

import capitalize from '../../../../utils/capitalize';
import ActionMenu from '../../../Shared/ActionMenu';

import HealtheraTable from '../../../Table/Table';
import campaignsAPI from '../../../../apis/campaigns';

import useLocationQuery from '../../../../hooks/useQuery';
import { SecondaryButton, DangerButton } from '../../../Shared/Button';
import SimpleModal from '../../../Modal/SimpleModal';

const DEFAULT_LIMIT = 10;

function LoadingCell({ height = 60 }) {
  return (
    <Cell height={height}>
      <LoadingText />
    </Cell>
  );
}

const headers = {
  view: {
    title: { title: 'Title', width: '20%' },
    body: { title: 'Body' },
    status: { title: 'Status', width: '5%' },
    date_sent: { title: 'Last Sent', width: '15%' },
    total: { title: 'Customers in List', width: '10%' },
    reach: { title: 'Customer Reach', width: '10%' },
    actions: { title: ' ', width: '5%' },
  },
  headers: true,
};

const prepareLoadingData = (limit) => {
  return Array.from({ length: limit }).reduce(
    (data) => {
      data.push({
        view: {
          title: <LoadingCell />,
          body: <LoadingCell />,
          status: <LoadingCell />,
          date_sent: <LoadingCell />,
          total: <LoadingCell />,
          reach: <LoadingCell />,
        },
      });
      return data;
    },
    [headers]
  );
};

const PushNotificationsTable = () => {
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [modalInProgress, setModalInProgress] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedCampaignId, setSelectedCampaignId] = useState('');

  const history = useHistory();
  const { changeLocationQuery, query } = useLocationQuery();
  const { enqueueSnackbar } = useSnackbar();

  const fetchCampaigns = useCallback(async () => {
    const { page = 0, limit = DEFAULT_LIMIT } = query;
    setLoading(true);

    const response = await campaignsAPI.getCampaigns(page, limit);
    if (response.data?.data) {
      setCampaigns(response.data?.data);
      setTotal(response.data?.pagination?.total);
    }
    setLoading(false);
  }, [query]);

  const handleSend = useCallback(async (id) => {
    try {
      await campaignsAPI.sendCampaign(id);
      enqueueSnackbar('Sending campaign!', { variant: 'success' });
      setModalInProgress(false);
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      fetchCampaigns();
    }
  }, [enqueueSnackbar, fetchCampaigns]);

  const handleDelete = useCallback(async (id) => {
    try {
      await campaignsAPI.deleteCampaign(id);
      enqueueSnackbar('Campaign deleted successfully!', { variant: 'success' });
      setModalInProgress(false);

      fetchCampaigns();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }, [enqueueSnackbar, fetchCampaigns]);

  const handleRefresh = () => fetchCampaigns();

  const menuOptions = (status) => [
    {
      key: 'send',
      label: 'Send',
      action: (id) => {
        setSelectedCampaignId(id);
        setModalInProgress(true);
        setModalType('send');
      },
      disabled: ['sent', 'sending'].includes(status),
    },
    {
      key: 'edit',
      label: status === 'sent' ? 'Details' : 'Edit',
      action: (id) => history.push(`/campaigns/${id}`),
      disabled: ['sending'].includes(status),
    },
    {
      key: 'delete',
      label: 'Delete',
      action: (id) => {
        setSelectedCampaignId(id);
        setModalInProgress(true);
        setModalType('delete');
      },
      color: 'red',
      disabled: ['sending'].includes(status),
    },
  ];

  const prepareData = (campaigns) => {
    if (!campaigns?.length) return [];

    return campaigns.reduce(
      (data, campaign) => {
        const { _id, title, body, date_sent, status, total, reach } = campaign;

        let modifiedMenuOptions = [...menuOptions(status)];

        if (status === 'sending') {
          modifiedMenuOptions.push({
            key: 'refresh',
            label: 'Refresh',
            action: (id) => handleRefresh(id),
            color: 'green',
          });
        }

        data.push({
          view: {
            title,
            body,
            status: status ? capitalize(status) : '',
            date_sent: date_sent ? moment(date_sent).format('ddd, MMM D, YYYY HH:mm') : '',
            total,
            reach,
            actions: <ActionMenu options={modifiedMenuOptions.map((option) => ({ ...option, action: () => option.action(_id) }))} />,
          },
        });

        return data;
      },
      [headers]
    );
  };

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handlePageChange = (_, page) => {
    const newQuery = { ...query };
    newQuery.page = page;

    changeLocationQuery(newQuery);
  };

  const handleRowsPerPageChange = ({ target }) => {
    const { value: rowsPerPage } = target;

    changeLocationQuery({
      ...query,
      limit: rowsPerPage,
    });
  };

  const preparedData = loading ? prepareLoadingData(query.limit) : prepareData(campaigns);

  return (
    <>
      {preparedData?.length > 0 ? (
        <HealtheraTable
          data={preparedData}
          totalCount={total}
          rowsPerPage={+query.limit || DEFAULT_LIMIT}
          rowsPerPageOptions={[10, 20, 50]}
          onChangePage={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={+query.page || 0}
          displayPagination
          noHover
        />
      ) : (
        <h3>No push notifications yet!</h3>
      )}
      <SimpleModal
        open={modalInProgress}
        onClose={() => setModalInProgress(false)}
        title={modalType === 'delete' ? 'Delete Campaign' : 'Send Campaign'}
        bodyText={
          modalType === 'delete' ? (
            <p>Are you sure you want to delete this campaign?</p>
          ) : (
            <p>Are you sure you want to send this campaign?</p>
          )
        }
      >
        <SecondaryButton onClick={() => setModalInProgress(false)}>Cancel</SecondaryButton>
        {modalType === 'delete' ? (
          <DangerButton onClick={() => handleDelete(selectedCampaignId)}>Delete</DangerButton>
        ) : (
          <DangerButton onClick={() => handleSend(selectedCampaignId)}>Send</DangerButton>
        )}
      </SimpleModal>
    </>
  );
};

const Cell = styled.div`
  ${(props) => `height: ${props.height}px`};
  display: flex;
  align-items: center;
`;

const loadAnimation = keyframes`
  from {
    opacity: 0.3;
  }

  to {
    opacity: 0.8;
  }
`;

const LoadingText = styled.p`
  margin: 0;
  animation: ${loadAnimation} 0.7s linear alternate infinite;
  width: 100%;
  height: 16px;
  background-color: rgba(200, 200, 200, 0.8);
  border-radius: 5px;
`;

export default PushNotificationsTable;
